import {
  generateUniqueCaseCode,
  normalizeCaseForDraft,
} from './buildTestPlanCasesFromImport';

export const TEST_PLAN_SCOPE = {
  ANNOTATION: 'annotation',
  ALL_ANNOTATIONS: 'all_annotations',
  FULL_CONVERSATION: 'full_conversation',
};

export const getConversationMessageRole = (message, conversation) => {
  if (message?.role) return message.role;
  if (message?.direction === 'inbound') return 'user';
  if (message?.direction === 'outbound') return 'assistant';
  if (conversation?.to_number && message?.from_number === conversation.to_number) {
    return 'assistant';
  }
  return 'user';
};

export const getConversationMessageContent = (message) => {
  const content = message?.message_content ?? message?.content ?? '';
  return String(content || '').trim();
};

export const isConversationTurnMessage = (message) => {
  if (!message) return false;
  if (message.is_appointment || message.is_leave_message || message.is_event) return false;
  return getConversationMessageContent(message).length > 0;
};

export const filterConversationTurnMessages = (messages) => (
  (Array.isArray(messages) ? messages : []).filter(isConversationTurnMessage)
);

export const filterMessagesByIds = (allMessages, selectedIds) => {
  const idSet = new Set(Array.isArray(selectedIds) ? selectedIds : []);
  if (idSet.size === 0) return [];
  return (Array.isArray(allMessages) ? allMessages : []).filter(
    (message) => idSet.has(message?.id)
  );
};

export const buildPreviewPairsFromConversationTurns = (messages, conversation) => {
  const pendingUserMessages = [];
  const pairs = [];
  const userMessages = [];

  filterConversationTurnMessages(messages).forEach((message) => {
    const role = String(getConversationMessageRole(message, conversation) || '').toLowerCase();
    const content = getConversationMessageContent(message);
    if (!content) return;

    if (role === 'user') {
      pendingUserMessages.push(content);
      userMessages.push(content);
      return;
    }

    if (role === 'assistant' && pendingUserMessages.length > 0) {
      const clientMessage = pendingUserMessages.shift();
      pairs.push({
        client_message: clientMessage,
        expected_result: content,
      });
    }
  });

  return { userMessages, pairs };
};

export const buildAnnotationTraceText = (annotation, conversationId) => {
  const title = String(annotation?.annotation_title || '').trim();
  const content = String(annotation?.annotation_content || '').trim();
  const convId = String(conversationId || annotation?.conversation_id || '').trim();
  let trace = title;
  if (content) {
    trace = trace ? `${trace} — ${content}` : content;
  }
  if (convId) {
    trace = trace ? `${trace} | ${convId}` : convId;
  }
  return trace;
};

export const buildTestPlanCaseFromMessages = ({
  messages,
  conversation,
  clid,
  existingCases,
  annotation = '',
  name,
  is_skipped = false,
}) => {
  const { userMessages, pairs } = buildPreviewPairsFromConversationTurns(messages, conversation);
  if (userMessages.length === 0) {
    throw new Error('No user messages found in the selected range.');
  }

  const builtCase = normalizeCaseForDraft({
    name: name || generateUniqueCaseCode(clid, existingCases),
    messages: userMessages,
    is_skipped,
    annotation: String(annotation || ''),
    preview_pairs: pairs,
  });

  return builtCase;
};

export const buildTestPlanCaseFromAnnotation = (
  annotation,
  allMessages,
  conversation,
  clid,
  existingCases
) => {
  const selectedIds = Array.isArray(annotation?.selected_message_ids)
    ? annotation.selected_message_ids
    : [];
  const filteredMessages = filterMessagesByIds(allMessages, selectedIds);
  if (filteredMessages.length === 0) {
    throw new Error('Annotation has no matching messages in this conversation.');
  }

  return buildTestPlanCaseFromMessages({
    messages: filteredMessages,
    conversation,
    clid,
    existingCases,
    annotation: buildAnnotationTraceText(annotation, conversation?.conversation_id),
  });
};

export const buildTestPlanCasesFromAnnotations = (
  annotations,
  allMessages,
  conversation,
  clid,
  existingCases = []
) => {
  const sourceAnnotations = Array.isArray(annotations) ? annotations : [];
  if (sourceAnnotations.length === 0) {
    throw new Error('No annotations available to convert.');
  }

  const accumulated = Array.isArray(existingCases) ? [...existingCases] : [];
  const newCases = [];

  sourceAnnotations.forEach((annotation) => {
    const builtCase = buildTestPlanCaseFromAnnotation(
      annotation,
      allMessages,
      conversation,
      clid,
      accumulated
    );
    accumulated.push(builtCase);
    newCases.push(builtCase);
  });

  return newCases;
};

export const buildTestPlanCaseFromFullConversation = (
  allMessages,
  conversation,
  clid,
  existingCases = []
) => buildTestPlanCaseFromMessages({
  messages: allMessages,
  conversation,
  clid,
  existingCases,
  annotation: String(conversation?.conversation_id || ''),
});

export const buildTestPlanCasesForScope = ({
  scope,
  annotation,
  annotations,
  allMessages,
  conversation,
  clid,
  existingCases = [],
}) => {
  if (scope === TEST_PLAN_SCOPE.ANNOTATION) {
    if (!annotation) {
      throw new Error('Select an annotation to add to the test plan.');
    }
    return [buildTestPlanCaseFromAnnotation(
      annotation,
      allMessages,
      conversation,
      clid,
      existingCases
    )];
  }

  if (scope === TEST_PLAN_SCOPE.ALL_ANNOTATIONS) {
    return buildTestPlanCasesFromAnnotations(
      annotations,
      allMessages,
      conversation,
      clid,
      existingCases
    );
  }

  if (scope === TEST_PLAN_SCOPE.FULL_CONVERSATION) {
    return [buildTestPlanCaseFromFullConversation(
      allMessages,
      conversation,
      clid,
      existingCases
    )];
  }

  throw new Error(`Unknown test plan scope: ${scope}`);
};

/** Flatten draft cases to CSV rows for Conversations export. */
export const flattenTestPlanCasesToCsvRows = (cases, conversationId) => {
  const rows = [];
  (Array.isArray(cases) ? cases : []).forEach((caseItem) => {
    const messages = Array.isArray(caseItem?.messages) ? caseItem.messages : [];
    const pairs = Array.isArray(caseItem?.preview_pairs) ? caseItem.preview_pairs : [];
    const groupId = String(caseItem?.annotation || conversationId || '');
    messages.forEach((message, idx) => {
      rows.push({
        conversation: groupId,
        userMessage: String(message || ''),
        aiMessage: String(pairs[idx]?.expected_result || ''),
      });
    });
  });
  return rows;
};
